import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useFormContext } from './Form';
import { useFieldError } from './utils';

export interface PasswordFieldProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'name' | 'id' | 'type'
> {
  /** RHF field name. Supports nested paths. */
  name: string;
  label: React.ReactNode;
  description?: React.ReactNode;
  /** Override the auto-generated id (optional). */
  id?: string;
}

export function PasswordField({
  name,
  label,
  description,
  id,
  className,
  ...inputProps
}: PasswordFieldProps) {
  const { register } = useFormContext();
  const error = useFieldError(name);
  const { t } = useTranslation();
  const [visible, setVisible] = React.useState(false);
  const fieldId = id ?? `f-${name.replace(/\W+/g, '-')}`;
  const errorId = `${fieldId}-error`;
  const descId = description ? `${fieldId}-desc` : undefined;
  const Icon = visible ? EyeOff : Eye;
  return (
    <div>
      <Label htmlFor={fieldId}>{label}</Label>
      <div className="relative">
        <Input
          id={fieldId}
          type={visible ? 'text' : 'password'}
          aria-invalid={error ? true : undefined}
          aria-describedby={
            [error ? errorId : undefined, descId].filter(Boolean).join(' ') || undefined
          }
          className={cn('pr-10', className)}
          {...register(name)}
          {...inputProps}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={t(visible ? 'auth.hidePassword' : 'auth.showPassword')}
          aria-pressed={visible}
          tabIndex={-1}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring rounded-r-md"
        >
          <Icon aria-hidden="true" className="h-4 w-4" />
        </button>
      </div>
      {description && (
        <p id={descId} className="mt-1 text-xs text-muted-foreground">
          {description}
        </p>
      )}
      {error && (
        <p id={errorId} className="mt-1 text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
